import { useState, useEffect, useRef, FormEvent, ChangeEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d73e6p4lqq77n94m7hdg';

const offerItems = [
  'Share your goals',
  'Talk through where you feel stuck',
  'Learn how coaching can help',
  'Take the first step toward real change',
];

interface FormData {
  name: string;
  email: string;
  phone: string;
  goals: string;
}

export default function ConsultationSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<FormData>({ name: '', email: '', phone: '', goals: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.goals.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        goals: formData.goals,
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', goals: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="consultation" className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Top CTA Block */}
        <div
          className={`bg-[#2D3E2E] rounded-3xl p-10 md:p-16 text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span
            className="text-xs font-semibold tracking-widest uppercase text-[#A8B5A0] block mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Free Consultation
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-white mb-10"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            Support Is Just a Click Away
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {offerItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-left">
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <i className="ri-check-line text-[#D4896A] text-xl" />
                </div>
                <span
                  className="text-base text-white/85"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {item}
                </span>
              </div>
            ))}
          </div>
          <a
            href="#consultation-form"
            className="inline-block bg-[#D4896A] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#c07a5a] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Claim Your Free Consultation
          </a>
        </div>

        {/* Form */}
        <div
          id="consultation-form"
          className={`max-w-2xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="bg-[#FAF8F5] rounded-3xl p-8 md:p-12">
            <h3
              className="text-2xl md:text-3xl font-bold text-[#2C2C2C] text-center mb-8"
              style={{ fontFamily: '\'Playfair Display\', serif' }}
            >
              Get More Info / Book Your Free Consultation
            </h3>

            {status === 'success' ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className="ri-checkbox-circle-line text-5xl text-[#D4896A]" />
                </div>
                <p
                  className="text-xl font-semibold text-[#2C2C2C] mb-2"
                  style={{ fontFamily: '\'Playfair Display\', serif' }}
                >
                  Thank you! We will be in touch soon.
                </p>
                <p className="text-[#6B6B6B] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Your first step toward a Brand New You is already taken.
                </p>
              </div>
            ) : (
              <form
                id="consultation-signup"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label
                    className="block text-sm font-medium text-[#2C2C2C] mb-2"
                    htmlFor="name"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Full Name <span className="text-[#D4896A]">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full h-12 px-4 border-2 border-[#E0D8D0] rounded-xl text-sm text-[#2C2C2C] bg-white focus:outline-none focus:border-[#D4896A] transition-colors placeholder:text-[#B0A898]"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-[#2C2C2C] mb-2"
                    htmlFor="email"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Email Address <span className="text-[#D4896A]">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full h-12 px-4 border-2 border-[#E0D8D0] rounded-xl text-sm text-[#2C2C2C] bg-white focus:outline-none focus:border-[#D4896A] transition-colors placeholder:text-[#B0A898]"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-[#2C2C2C] mb-2"
                    htmlFor="phone"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 000-0000"
                    className="w-full h-12 px-4 border-2 border-[#E0D8D0] rounded-xl text-sm text-[#2C2C2C] bg-white focus:outline-none focus:border-[#D4896A] transition-colors placeholder:text-[#B0A898]"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium text-[#2C2C2C] mb-2"
                    htmlFor="goals"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Tell Us About Your Goals
                  </label>
                  <textarea
                    id="goals"
                    name="goals"
                    value={formData.goals}
                    onChange={handleChange}
                    placeholder="Share a bit about where you are and where you want to go..."
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-[#E0D8D0] rounded-xl text-sm text-[#2C2C2C] bg-white focus:outline-none focus:border-[#D4896A] transition-colors resize-none placeholder:text-[#B0A898]"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                  <p className="text-xs text-[#B0A898] text-right mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formData.goals.length}/500
                  </p>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || formData.goals.length > 500}
                  className="w-full bg-[#D4896A] text-white py-4 rounded-xl font-semibold text-base hover:bg-[#c07a5a] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {status === 'sending' ? 'Sending...' : 'Get Started Today'}
                </button>

                <p
                  className="text-xs text-[#6B6B6B] text-center italic"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  No pressure. No obligation. Just your first step toward positive change.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
